import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { OrderModel } from "@insite/client-framework/Types/ApiModels";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
});

type Props = {
    order?: OrderModel;
} & ReturnType<typeof mapStateToProps>;

export interface OrderStatusInformationStyles {
    informationWrapper?: InjectableCss;
    wrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    orderNumberText?: TypographyPresentationProps;
    statusText?: TypographyPresentationProps;
    orderDateText?: TypographyPresentationProps;
    termsText?: TypographyPresentationProps;
    poNumberText?: TypographyPresentationProps;
    shippingMethodText?: TypographyPresentationProps;
    vatNumberWrapper?: InjectableCss;
    vatNumberText?: TypographyPresentationProps;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
}

export const orderStatusInformationStyles: OrderStatusInformationStyles = {
    informationWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    wrapper: {
        css: css`
            margin-right: 25px;
            display: flex;
            gap: 5px;
            font-size: 18px;

            @media screen and (max-width: 500px) {
                margin-right: 0;
            }
        `,
    },
    titleText: {
        weight: 600,
        size: 18,
    },
    vatNumberWrapper: {
        css: css`
            margin-top: 10px;
        `,
    },
};

const styles = orderStatusInformationStyles;

const OrderInformation = ({ order, language }: Props) => {
    if (!order) {
        return null;
    }

    const parseOrderType = (orderType = "") => {
        switch (orderType.toUpperCase()) {
            case "CARS":
                return translate("AIR Order");
            case "EDI":
                return translate("EDI Order");
            case "OCR":
                return translate("Sales Rep");
            case "WEB":
                return translate("Web Order");
            case "SA":
                return translate("Scheduled Order");
            default:
                return "Sales Rep";
        }
    };

    return (
        <>
            <StyledWrapper {...styles.informationWrapper} data-test-selector="orderStatusInformation">
                <SmallHeadingAndText
                    extendedStyles={styles.headingAndTextStyles}
                    heading={`${translate("Order #")}: `}
                    text={order.erpOrderNumber || translate("Pending")}
                />

                {order.webOrderNumber && (
                    <SmallHeadingAndText
                        extendedStyles={styles.headingAndTextStyles}
                        heading={`${translate("Web Conf. #")}: `}
                        text={order.webOrderNumber}
                    />
                )}

                <SmallHeadingAndText
                    extendedStyles={styles.headingAndTextStyles}
                    heading={`${translate("Order type")}: `}
                    text={parseOrderType(order.properties?.orderChannel)}
                />

                <SmallHeadingAndText
                    extendedStyles={styles.headingAndTextStyles}
                    heading={`${translate("Order Date")}: `}
                    text={
                        order.orderDate ? getLocalizedDateTime({ dateTime: new Date(order.orderDate), language }) : ""
                    }
                />

                <SmallHeadingAndText
                    extendedStyles={styles.headingAndTextStyles}
                    heading={`${translate("Order Status")}: `}
                    text={order.status}
                />

                <SmallHeadingAndText
                    extendedStyles={styles.headingAndTextStyles}
                    heading={`${translate("Customer P.O")}: `}
                    text={order.customerPO}
                />
            </StyledWrapper>
        </>
    );
};

export default connect(mapStateToProps)(OrderInformation as any) as any;
